import React from 'react';
import {Box, Grid} from "@mui/material";
import PollRangeTagBox from "../../components/PollRangeTagBox";
import {VoterOverview} from "../../components/appointment/VoterOverview";
import AlfyPollCalendar from "../../components/poll/AlfyPollCalendar";
import {CurrentVotedBox} from "../../components/poll/CurrentVotedBox";
import {AlfyButton} from "../../components/AlfyButton";
import {useRecoilState, useRecoilValue} from "recoil";
import {PollPayload} from "../../api/AlfyApiClient";
import AttendType from "../../models/AttendType";
import {useNavigate, useParams} from "react-router-dom";
import {alfyToast, isSameDate} from "../../utils/Utils";
import {userState} from "../../states/user/userAtom";
import {fromCreatePageState} from "../../states/appointment/createAppointmentAtom";
import {AlfyBorderButton} from "../../components/AlfyBorderButton";
import {useFetchAppointmentWithPoll} from "../../states/appointment/server";
import {useAlfyApi} from "../../hooks/alfyHooks";
import {useFetchMemberMe, useJoinAppointment} from "../../states/member/server";
import {JoinAppointmentRequest} from "../../models/request/member";

export interface AttendPollStepPageProps {
    setStep: React.Dispatch<React.SetStateAction<number>>;
    selectedAbsentDates: Date[];
    selectedAttendDates: Date[];
    setSelectedAbsentDates: React.Dispatch<React.SetStateAction<Date[]>>;
    setSelectedAttendDates: React.Dispatch<React.SetStateAction<Date[]>>;
}

export const AttendPollStepPage: React.FC<AttendPollStepPageProps> = ({ setStep, selectedAttendDates, selectedAbsentDates, setSelectedAttendDates, setSelectedAbsentDates }) => {
    // states
    const [fromCreatePage, setFromCreatePage] = useRecoilState(fromCreatePageState);
    const user = useRecoilValue(userState);

    // hooks
    const { id: appointmentId } = useParams()
    const { data: appointment } = useFetchAppointmentWithPoll(appointmentId ?? '')
    const { data: memberMe } = useFetchMemberMe(appointmentId ?? '')
    const navigate = useNavigate()

    // mutations
    const { usePollMultiple } = useAlfyApi()
    const { mutateAsync: joinAppointment } = useJoinAppointment()
    const { mutateAsync: pollMultiple } = usePollMultiple(appointmentId!)

    const handleSubmit = async () => {
        if (!appointment) {
            alfyToast("약속 정보를 불러오는 중입니다. 잠시만 기다려주세요.")
            return;
        }

        if (!user) {
            alfyToast("로그인 후 이용해주세요.")
            navigate(`/login?redirect=/appointments/${appointmentId}/poll`)
            return
        }

        if (!memberMe) {
            const request: JoinAppointmentRequest = {
                appointmentId: appointment.id,
                name: user.name,
                role: fromCreatePage ? "ORGANIZER" : "MEMBER"
            }
            try {
                await joinAppointment(request)
                setFromCreatePage(false)
            } catch (e) {
                alfyToast("약속 참여에 실패했습니다. 잠시 뒤 다시 시도해주세요.")
                return
            }
        }

        // TODO 효율화
        let payload: PollPayload[] = []
        const attendAndAbsentIds = new Set<string>();

        // 되는 날
        selectedAttendDates.forEach((date) => {
            const pollItem = appointment!.pollItems.find(pollItem => isSameDate(pollItem.date, date));
            if (pollItem) {
                attendAndAbsentIds.add(pollItem.id);
                payload = [...payload, { pollItemId: pollItem.id, attendType: AttendType.ATTEND }];
            }
        });

        // 안되는 날
        selectedAbsentDates.forEach((date) => {
            const pollItem = appointment!.pollItems.find(pollItem => isSameDate(pollItem.date, date));
            if (pollItem) {
                attendAndAbsentIds.add(pollItem.id);
                payload = [...payload, { pollItemId: pollItem.id, attendType: AttendType.ABSENT }];
            }
        });

        // 확실치 않음(투표 안함)
        appointment!.pollItems.forEach((pollItem) => {
            if (!attendAndAbsentIds.has(pollItem.id)) {
                payload = [...payload, { pollItemId: pollItem.id, attendType: AttendType.NOT_SURE }];
            }
        });

        try {
            await pollMultiple(payload)
            navigate(`/appointments/${appointment!.id}`, { replace: true });
            alfyToast("투표가 완료되었습니다. 다른 약속 멤버도 초대해보세요.")
        } catch (error) {
            alfyToast("투표에 실패했습니다. 잠시 뒤 다시 시도해주세요.")
        }
    }

    function handleBack() {
        setSelectedAttendDates([]);
        setStep(0);
    }

    if (!appointment) {
        return <></>
    }

    return (
        <div className={"fullscreen"}>
            <Box height={"30px"}/>
            <div className="container">
                <span className={"primary-title-text"}><span style={{color: '#38D59C'}}>확실히 되는 날</span>을<br/>캘린더에서 전부 골라주세요</span>
                <Box height={"8px"}/>
                <PollRangeTagBox appointment={appointment!} />
            </div>
            <Box height={"20px"}/>
            <VoterOverview voters={appointment!.getVotedMembers()} />
            <Box height={"20px"}/>
            <AlfyPollCalendar
                activeDates={appointment.getActiveDates()}
                datePollInfos={appointment!.toDatePollInfos()}
                isSelectingAttend={true}
                disabledDates={selectedAbsentDates}
                selectedAbsentDates={selectedAbsentDates}
                selectedAttendDates={selectedAttendDates}
                setSelectedAbsentDates={setSelectedAbsentDates}
                setSelectedAttendDates={setSelectedAttendDates}
            />
            <Box height={"20px"}/>
            <CurrentVotedBox absentDates={selectedAbsentDates} attendDates={selectedAttendDates} />
            {!(selectedAbsentDates.length === 0 && selectedAttendDates.length === 0) && <Box height={"30px"} />}
            <Grid container spacing={1}>
                <Grid item xs>
                    <AlfyButton text={"다 골랐어요"} onClick={handleSubmit} />
                </Grid>
                <Grid item xs>
                    <AlfyBorderButton text={"안되는 날 다시고르기"} onClick={() => handleBack()} />
                </Grid>
            </Grid>
            <Box height={"20px"}/>
        </div>
    )
}